import { useState, useEffect, useMemo } from 'react';
import { cloudpePlansApi } from '../lib/api';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Check,
  X,
  AlertCircle,
  Cpu,
  HardDrive,
  Monitor,
  Layers,
  IndianRupee,
} from 'lucide-react';

const PLAN_TYPES = [
  { value: 'general_purpose', label: 'General Purpose' },
  { value: 'cpu_intensive', label: 'CPU Intensive' },
  { value: 'memory_optimized', label: 'Memory Optimized' },
];

const OS_TYPES = [
  { value: 'linux', label: 'Linux' },
  { value: 'windows', label: 'Windows' },
];

const defaultPlan = {
  plan_type: 'general_purpose',
  flavour: '',
  os_type: 'linux',
  vcpu: 1,
  ram_gb: 4,
  base_storage_gb: 30,
  monthly_price_inr: 0,
  is_active: true,
};

// Tab button component - defined outside to prevent re-creation on each render
function TabButton({ active, onClick, children, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
        active
          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
      }`}
    >
      {children}
      {count !== undefined && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${active ? 'bg-amber-500/30' : 'bg-slate-700'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Filters
  const [selectedOS, setSelectedOS] = useState('linux');
  const [selectedPlanType, setSelectedPlanType] = useState('general_purpose');

  // Edit mode
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState(defaultPlan);

  // Add mode
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlan, setNewPlan] = useState(defaultPlan);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Saving state
  const [saving, setSaving] = useState(false);

  // Load plans
  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await cloudpePlansApi.list();
      setPlans(data.plans || []);
    } catch (err) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  // Filter plans
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      const matchesOS = plan.os_type === selectedOS;
      const matchesType = plan.plan_type === selectedPlanType;
      const matchesSearch = searchTerm === '' ||
        plan.flavour.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesOS && matchesType && matchesSearch;
    });
  }, [plans, selectedOS, selectedPlanType, searchTerm]);

  // Plan counts by type
  const planCounts = useMemo(() => {
    const counts = {};
    plans.forEach(plan => {
      const key = `${plan.os_type}_${plan.plan_type}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [plans]);

  // Handle add plan
  const handleAddPlan = async () => {
    if (!newPlan.flavour || !newPlan.monthly_price_inr) {
      setError('Flavour and price are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data = await cloudpePlansApi.create({
        ...newPlan,
        os_type: selectedOS,
        plan_type: selectedPlanType,
      });
      setPlans([...plans, data.plan]);
      setShowAddForm(false);
      setNewPlan(defaultPlan);
      setSuccess('Plan added successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to add plan');
    } finally {
      setSaving(false);
    }
  };

  // Handle edit plan
  const startEdit = (plan) => {
    setEditingPlan(plan.id);
    setEditForm({ ...plan });
  };

  const cancelEdit = () => {
    setEditingPlan(null);
    setEditForm(defaultPlan);
  };

  const saveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const data = await cloudpePlansApi.update(editingPlan, editForm);
      setPlans(plans.map(p => p.id === editingPlan ? data.plan : p));
      setEditingPlan(null);
      setSuccess('Plan updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete plan
  const handleDelete = async (id) => {
    setSaving(true);
    setError('');
    try {
      await cloudpePlansApi.delete(id);
      setPlans(plans.filter(p => p.id !== id));
      setDeleteConfirm(null);
      setSuccess('Plan deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete plan');
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Layers className="w-7 h-7 text-amber-500" />
            CloudPE Pricing Plans
          </h1>
          <p className="text-slate-400 mt-1">Manage pre-defined VM flavor pricing</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600
            hover:from-amber-600 hover:to-orange-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-amber-500/20"
        >
          <Plus className="w-5 h-5" />
          Add Plan
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400">
          <Check className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* OS Tabs */}
      <div className="flex gap-2 mb-4">
        {OS_TYPES.map(os => (
          <TabButton
            key={os.value}
            active={selectedOS === os.value}
            onClick={() => setSelectedOS(os.value)}
            count={plans.filter(p => p.os_type === os.value).length}
          >
            <Monitor className="w-4 h-4" />
            {os.label}
          </TabButton>
        ))}
      </div>

      {/* Plan Type Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {PLAN_TYPES.map(type => (
          <TabButton
            key={type.value}
            active={selectedPlanType === type.value}
            onClick={() => setSelectedPlanType(type.value)}
            count={planCounts[`${selectedOS}_${type.value}`] || 0}
          >
            {type.label}
          </TabButton>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by flavour..."
          className="w-full bg-slate-900/50 border border-slate-800/50 rounded-xl pl-12 pr-4 py-3 text-slate-200
            placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20"
        />
      </div>

      {/* Add Plan Form */}
      {showAddForm && (
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Add New Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block uppercase tracking-wide">Flavour</label>
              <input
                type="text"
                value={newPlan.flavour}
                onChange={(e) => setNewPlan({ ...newPlan, flavour: e.target.value })}
                placeholder="a.gp1.4g"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-slate-200
                  focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block uppercase tracking-wide">vCPU</label>
              <input
                type="number"
                value={newPlan.vcpu}
                onChange={(e) => setNewPlan({ ...newPlan, vcpu: parseInt(e.target.value) || 1 })}
                min="1"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-slate-200
                  focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block uppercase tracking-wide">RAM (GB)</label>
              <input
                type="number"
                value={newPlan.ram_gb}
                onChange={(e) => setNewPlan({ ...newPlan, ram_gb: parseInt(e.target.value) || 1 })}
                min="1"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-slate-200
                  focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block uppercase tracking-wide">Storage (GB)</label>
              <input
                type="number"
                value={newPlan.base_storage_gb}
                onChange={(e) => setNewPlan({ ...newPlan, base_storage_gb: parseInt(e.target.value) || 30 })}
                min="0"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-slate-200
                  focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block uppercase tracking-wide">Price (INR/mo)</label>
              <input
                type="number"
                value={newPlan.monthly_price_inr}
                onChange={(e) => setNewPlan({ ...newPlan, monthly_price_inr: parseFloat(e.target.value) || 0 })}
                min="0"
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-slate-200
                  focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleAddPlan}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewPlan(defaultPlan); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plans Table */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-800/50 bg-slate-900/30">
                <th className="py-4 px-4 text-left font-medium">Flavour</th>
                <th className="py-4 px-4 text-center font-medium">
                  <div className="flex items-center justify-center gap-1">
                    <Cpu className="w-4 h-4" /> vCPU
                  </div>
                </th>
                <th className="py-4 px-4 text-center font-medium">RAM</th>
                <th className="py-4 px-4 text-center font-medium">
                  <div className="flex items-center justify-center gap-1">
                    <HardDrive className="w-4 h-4" /> Storage
                  </div>
                </th>
                <th className="py-4 px-4 text-right font-medium">
                  <div className="flex items-center justify-end gap-1">
                    <IndianRupee className="w-4 h-4" /> Price/mo
                  </div>
                </th>
                <th className="py-4 px-4 text-center font-medium">Status</th>
                <th className="py-4 px-4 text-center font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    No plans found for this category
                  </td>
                </tr>
              ) : (
                filteredPlans.map(plan => (
                  <tr key={plan.id} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                    {editingPlan === plan.id ? (
                      // Edit mode
                      <>
                        <td className="py-3 px-4">
                          <input
                            type="text"
                            value={editForm.flavour}
                            onChange={(e) => setEditForm({ ...editForm, flavour: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editForm.vcpu}
                            onChange={(e) => setEditForm({ ...editForm, vcpu: parseInt(e.target.value) || 1 })}
                            className="w-20 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-center"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editForm.ram_gb}
                            onChange={(e) => setEditForm({ ...editForm, ram_gb: parseInt(e.target.value) || 1 })}
                            className="w-20 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-center"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editForm.base_storage_gb}
                            onChange={(e) => setEditForm({ ...editForm, base_storage_gb: parseInt(e.target.value) || 30 })}
                            className="w-20 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-center"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <input
                            type="number"
                            value={editForm.monthly_price_inr}
                            onChange={(e) => setEditForm({ ...editForm, monthly_price_inr: parseFloat(e.target.value) || 0 })}
                            className="w-28 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-right"
                          />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              editForm.is_active
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}
                          >
                            {editForm.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={saveEdit}
                              disabled={saving}
                              className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded transition-all"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-1.5 text-slate-400 hover:bg-slate-700 rounded transition-all"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // View mode
                      <>
                        <td className="py-3 px-4">
                          <span className="font-mono text-amber-400">{plan.flavour}</span>
                        </td>
                        <td className="py-3 px-4 text-center text-slate-200">{plan.vcpu}</td>
                        <td className="py-3 px-4 text-center text-slate-200">{plan.ram_gb} GB</td>
                        <td className="py-3 px-4 text-center text-slate-200">{plan.base_storage_gb} GB</td>
                        <td className="py-3 px-4 text-right font-mono text-slate-200">
                          {formatPrice(plan.monthly_price_inr)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            plan.is_active
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {deleteConfirm === plan.id ? (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleDelete(plan.id)}
                                disabled={saving}
                                className="px-2 py-1 text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded transition-all"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-2 py-1 text-xs bg-slate-700 text-slate-300 hover:bg-slate-600 rounded transition-all"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => startEdit(plan)}
                                className="p-1.5 text-slate-400 hover:text-amber-400 hover:bg-slate-800 rounded transition-all"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(plan.id)}
                                className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 flex items-center justify-between text-sm text-slate-400">
        <span>
          Showing {filteredPlans.length} of {plans.length} plans
        </span>
        <span>
          {OS_TYPES.find(o => o.value === selectedOS)?.label} &bull; {PLAN_TYPES.find(t => t.value === selectedPlanType)?.label}
        </span>
      </div>
    </div>
  );
}
